import { z } from 'zod'

/**
 * Supported content languages. Admins choose which are enabled and which one is primary (Site Settings).
 * Append-only: the order is the last-resort fallback order in `resolveLocalized`, so new languages go at the end.
 */
export const LOCALES = ['en', 'id', 'zh', 'vi', 'ja'] as const
export const LocaleSchema = z.enum(LOCALES)
export type Locale = z.infer<typeof LocaleSchema>
export const DEFAULT_LOCALE: Locale = 'en'

export const LOCALE_LABELS: Record<Locale, string> = { en: 'English', id: 'Bahasa Indonesia', zh: '中文', vi: 'Tiếng Việt', ja: '日本語' }

/** The locale a value is requested in, plus the primary locale it falls back to. */
export type LocaleContext = { locale: Locale; primary: Locale }

/** One optional value per locale. Stored documents written before a language existed simply lack its key. */
const localizedOf = <T extends z.ZodType>(value: T) =>
  z.object({ en: value.optional(), id: value.optional(), zh: value.optional(), vi: value.optional(), ja: value.optional() })

/**
 * A translatable string. Any locale may be missing or empty; readers fall back to the
 * primary locale (see `resolveLocalized`). Only the primary locale is ever required, and
 * that is checked against Site Settings at write time, not here.
 */
export const LocalizedSchema = localizedOf(z.string().max(250_000))
export type Localized = z.infer<typeof LocalizedSchema>

/** A translatable media reference (one media id per locale, or null). */
export const LocalizedMediaSchema = localizedOf(z.string().min(1).max(64).nullable())
export type LocalizedMedia = z.infer<typeof LocalizedMediaSchema>

/** A translatable ordered media list (e.g. a product gallery). */
export const LocalizedMediaListSchema = localizedOf(z.array(z.string().min(1).max(64)).max(30))
export type LocalizedMediaList = z.infer<typeof LocalizedMediaListSchema>

export const emptyLocalized = (): Localized => ({})
export const emptyLocalizedMedia = (): LocalizedMedia => ({})
export const emptyLocalizedMediaList = (): LocalizedMediaList => ({})

type LocaleRecord<T> = Partial<Record<Locale, T>>

const isEmptyValue = (v: unknown) =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0)

/** True when `locale` has its own (non-empty) value, i.e. is actually translated. */
export const hasTranslation = (value: LocaleRecord<unknown> | null | undefined, locale: Locale) =>
  !isEmptyValue(value?.[locale])

/**
 * Fallback chain: requested locale → primary locale → any other locale with a value
 * (in LOCALES order). The last step keeps content visible right after an admin switches
 * the primary to a language that isn't fully translated yet.
 */
export function resolveLocalized<T>(value: LocaleRecord<T> | null | undefined, ctx: LocaleContext): T | undefined {
  if (!value) return undefined
  for (const l of [ctx.locale, ctx.primary, ...LOCALES]) {
    const v = value[l]
    if (!isEmptyValue(v)) return v
  }
  return undefined
}

export const resolveText = (value: Localized | null | undefined, ctx: LocaleContext): string =>
  (resolveLocalized(value, ctx) ?? '').trim()

export const resolveMediaId = (value: LocalizedMedia | null | undefined, ctx: LocaleContext): string | null =>
  resolveLocalized(value, ctx) ?? null

export const resolveMediaList = (value: LocalizedMediaList | null | undefined, ctx: LocaleContext): string[] =>
  resolveLocalized(value, ctx) ?? []

/** Every media id referenced in any locale — for usage checks and bulk loading. */
export const allMediaIds = (value: LocalizedMedia | LocalizedMediaList | null | undefined): string[] =>
  Object.values(value ?? {}).flatMap((v) => (Array.isArray(v) ? v : v ? [v] : []))

export const SlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and single hyphens only')

export const IdSchema = z.string().min(1).max(64)

export const PublishStatusSchema = z.enum(['draft', 'published', 'archived'])
export type PublishStatus = z.infer<typeof PublishStatusSchema>

/** Per-document SEO. Empty fields fall back to the document's own title/summary, then Site Settings. */
export const SeoSchema = z.object({
  title: LocalizedSchema.default(emptyLocalized),
  description: LocalizedSchema.default(emptyLocalized),
  ogImage: LocalizedMediaSchema.default(emptyLocalizedMedia),
  canonicalPath: z.string().max(300).nullable().default(null),
  noindex: z.boolean().default(false),
})
export type Seo = z.infer<typeof SeoSchema>

export const FaqSchema = z.object({
  question: LocalizedSchema,
  answer: LocalizedSchema,
})
export type Faq = z.infer<typeof FaqSchema>

export const LinkSchema = z.object({
  label: LocalizedSchema,
  href: z.string().max(500),
})
export type Link = z.infer<typeof LinkSchema>

export const ListQuerySchema = z.object({
  q: z.string().max(200).optional(),
  status: PublishStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
})
export type ListQuery = z.infer<typeof ListQuerySchema>

export const paginated = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  })

/** Error envelope — every non-2xx API response has this shape. */
export const ErrorCodeSchema = z.enum([
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  /** The sign-in captcha (Turnstile) was missing or did not pass (#228). */
  'captcha_failed',
  'plan_limit',
  'validation_failed',
  'payload_too_large',
  'unsupported_media_type',
  'misconfig',
  'internal_error',
  /** A service the API called on the user's behalf (e.g. Anthropic) failed or refused. */
  'upstream_error',
])
export type ErrorCode = z.infer<typeof ErrorCodeSchema>

export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    issues: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
})
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>
